<script setup lang="ts">
import { computed } from "vue";

import type { MediaItem } from "@/types/album";

import { formatClock } from "@/lib/format";

/**
 * Port de `.media-card` (design-reference/styles.css).
 *
 * La rotation aléatoire (`nth-child`) et sa remise à plat au survol portent
 * sur la position de la carte dans la grille : ces règles vivent dans
 * `GalleryGrid.vue`, pas ici, pour que le sélecteur `nth-child` voie bien
 * les cartes voisines.
 */

const props = withDefaults(defineProps<{ item: MediaItem; highlightedId?: string | null }>(), {
  highlightedId: null,
});
const emit = defineEmits<{ select: [id: string] }>();

const backgroundImage = computed(() => `url("${props.item.previewUrl}")`);
const backgroundPosition = computed(() => props.item.crop?.position ?? "center");
const backgroundSize = computed(() => props.item.crop?.size ?? "cover");

const spanClass = computed(() => (props.item.span ? `media-card--${props.item.span}` : ""));

/** Fraîchement ajoutée : rejoue l'entrée « nouveau polaroïd » de la maquette. */
const isHighlighted = computed(() => props.item.id === props.highlightedId);

const timeLabel = computed(() => formatClock(props.item.createdAt));

const ariaLabel = computed(() => {
  const kind = props.item.kind === "video" ? "Vidéo" : "Photo";
  return `${kind} de ${props.item.author} : ${props.item.caption}`;
});
</script>

<template>
  <button
    type="button"
    class="media-card"
    :class="[spanClass, { 'is-new': isHighlighted }]"
    data-testid="media-card"
    :data-media-id="item.id"
    :data-media-origin="item.origin"
    :aria-label="ariaLabel"
    @click="emit('select', item.id)"
  >
    <span class="media-card__image" aria-hidden="true"></span>

    <template v-if="item.kind === 'video'">
      <span class="play-badge" aria-hidden="true">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m9 7 8 5-8 5V7Z"></path>
        </svg>
      </span>
      <span v-if="item.durationLabel" class="duration">{{ item.durationLabel }}</span>
    </template>

    <span class="media-card__caption">{{ item.caption }}</span>

    <span class="media-card__overlay">
      <span
        ><strong>{{ item.author }}</strong
        ><small>{{ timeLabel }}</small></span
      >
      <i aria-hidden="true">{{ item.liked ? "♥" : "♡" }}</i>
    </span>
  </button>
</template>

<style scoped>
.media-card {
  position: relative;
  display: inline-flex;
  align-items: stretch;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: auto;
  margin: 0 0 18px;
  padding: 7px 7px 13px;
  color: var(--color-ink);
  border: 0;
  border-radius: 1px;
  background: var(--color-ivory);
  box-shadow: var(--shadow-polaroid);
  break-inside: avoid;
  overflow: hidden;
  text-align: left;
  transition:
    transform 0.32s ease,
    box-shadow 0.32s ease;
  animation: var(--animate-card-in);
}

.media-card:hover,
.media-card:focus-visible {
  z-index: 3;
  outline: none;
  box-shadow: var(--shadow-polaroid-lift);
}

.media-card.is-new {
  animation: var(--animate-new-polaroid);
}

.media-card__image {
  position: relative;
  display: block;
  order: 1;
  width: 100%;
  height: 172px;
  flex: 0 0 auto;
  background-image: v-bind(backgroundImage);
  background-position: v-bind(backgroundPosition);
  background-size: v-bind(backgroundSize);
  transition: transform 0.5s ease;
}

.media-card--tall .media-card__image {
  height: 226px;
}

.media-card--wide .media-card__image,
.media-card--hero .media-card__image {
  height: 190px;
}

.media-card:hover .media-card__image {
  transform: none;
}

.media-card__caption {
  order: 2;
  padding: 11px 5px 3px;
  color: var(--color-olive);
  font-family: var(--font-script);
  font-size: clamp(21px, 5.6vw, 29px);
  line-height: 1;
}

.media-card__overlay {
  position: static;
  display: flex;
  order: 3;
  justify-content: space-between;
  align-items: center;
  padding: 4px 5px 2px;
  color: var(--color-muted);
  background: none;
}

.media-card__overlay span,
.media-card__overlay strong,
.media-card__overlay small {
  display: inline;
}

.media-card__overlay strong {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.media-card__overlay strong::before {
  content: "Ajouté par ";
}

/* Couleur pleine plutôt que le gris translucide d'origine, qui ne tenait que
   2,55:1 sur le carton du polaroïd — le pire contraste de toute l'interface,
   sur une mention pourtant présente sur chaque carte. */
.media-card__overlay small {
  margin-left: 5px;
  color: var(--color-muted);
  font-size: 10px;
  letter-spacing: 0.03em;
}

.media-card__overlay small::before {
  content: "· ";
}

.media-card__overlay > i {
  color: var(--color-rose);
  font-family: var(--font-serif);
  font-size: 17px;
  font-style: normal;
}

.play-badge {
  position: absolute;
  top: 75px;
  left: 50%;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgb(255 255 255 / 0.45);
  border-radius: 50%;
  background: rgb(25 31 27 / 0.42);
  backdrop-filter: blur(7px);
  transform: translate(-50%, -50%);
}

.play-badge svg {
  width: 18px;
  fill: white;
}

.duration {
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 4px 7px;
  border-radius: 99px;
  background: rgb(20 25 22 / 0.57);
  font-size: 11px;
}

@media (min-width: 760px) {
  .media-card {
    margin-bottom: 29px;
    padding: 10px 10px 16px;
  }

  .media-card__image {
    height: 220px;
  }

  .media-card--tall .media-card__image {
    height: 310px;
  }

  .media-card--wide .media-card__image,
  .media-card--hero .media-card__image {
    height: 245px;
  }

  .media-card__caption {
    font-size: 28px;
  }

  .play-badge {
    top: 100px;
  }
}
</style>
